<template>
    <div>
        <SToolbar>
            <v-icon small class="mr-1">ic-20 ic-magazine ic-col-grey</v-icon>
            <v-toolbar-title>{{ $t('magazin') }}</v-toolbar-title>
        </SToolbar>
        <v-card class="overflow-hidden">
            <v-navigation-drawer
                v-model="showErwSuche"
                absolute
                bottom
                temporary
                right
                width="530px"
                color="kp-light-panel-bg"
            >
                <v-toolbar dense class="kp-sublime-toolbar kp-root-panel-bg" elevation="0">
                    <v-toolbar-title>{{ $t('erw_suche') }}</v-toolbar-title>
                    <v-spacer />
                    <IconBtn icon-cls="x" icon-color="grey" @click="showErwSuche = false" />
                </v-toolbar>
                <MagazinEnhancedSearch
                    :initial-params="extendedSearchParams"
                    @search="onEnhancedSearch"
                    @reset="resetEnhancedSearch"
                />
            </v-navigation-drawer>
            <BaseRemoteList
                ref="magazinList"
                v-model="selectedItems"
                class="magazin-list"
                session-key="magazin-list"
                :subtitle="$t('magazinbeitraege')"
                :collection-class="MagazinCollection"
                :headers="headers"
                :filters="filters"
                :extra-params="extraParams"
                :options="{ sortBy: ['order_number'], sortDesc: [true] }"
                :item-class="() => 'magazin-list-row'"
                cache-data
                show-select
                :show-select-all="false"
                height="var(--usable-height-with-double-toolbar)"
                @loading="loading = $event"
                @edit-item="onEdit"
                @new-item="onNew"
                @dblclick:row="onRowDblClick"
                @item-selected="onItemSelected"
                @loaded="onLoaded"
            >
                <template #toolbar-items>
                    <IconBtn
                        color="primary"
                        :disabled="loading"
                        icon-cls="delete"
                        icon-color="white"
                        icon-disabled-color="white"
                        :tooltip="$t('soft_delete')"
                        @click.stop="showSoftDeleteDialog"
                    />
                    |
                    <v-btn-toggle
                        v-model="toggleFilters"
                        multiple
                        class="ml-2"
                        borderless
                        tile
                        background-color="primary"
                    >
                        <IconBtn
                            value="btn_reg"
                            color="primary"
                            :disabled="loading"
                            icon-cls="typed"
                            icon-color="white"
                            icon-disabled-color="grey"
                            :tooltip="$t('getippt')"
                        />
                        <IconBtn
                            value="btn_front"
                            color="primary"
                            :disabled="loading"
                            icon-cls="inwork-draft"
                            icon-color="white"
                            icon-disabled-color="grey"
                            :tooltip="$t('front_header')"
                        />
                        <IconBtn
                            value="btn_mar"
                            color="primary"
                            :disabled="loading"
                            icon-cls="tonewsletter"
                            icon-color="white"
                            icon-disabled-color="grey"
                            :tooltip="$t('fuer_nl')"
                        />
                    </v-btn-toggle>
                    <v-btn-toggle v-model="isPublishedFilter" class="ml-2" borderless tile background-color="primary">
                        <IconBtn
                            value="is_published"
                            color="primary"
                            :disabled="loading"
                            icon-cls="published"
                            icon-color="white"
                            icon-disabled-color="grey"
                            :tooltip="$t('publiziert')"
                        />
                        <IconBtn
                            value="is_published_future"
                            color="primary"
                            :disabled="loading"
                            icon-cls="hourglass"
                            icon-color="white"
                            icon-disabled-color="grey"
                            :tooltip="$t('publiziert_future')"
                        />
                        <IconBtn
                            value="is_not_published"
                            color="primary"
                            :disabled="loading"
                            icon-cls="pencil"
                            icon-color="white"
                            icon-disabled-color="grey"
                            :tooltip="$t('nicht_publiziert')"
                        />
                    </v-btn-toggle>
                    |
                    <v-btn-toggle
                        v-model="toggleFilters"
                        multiple
                        class="ml-2"
                        borderless
                        tile
                        background-color="primary">
                        <IconBtn
                            value="selectedItems"
                            color="primary"
                            :disabled="loading"
                            icon-cls="markedevents"
                            icon-color="white"
                            icon-disabled-color="grey"
                            :tooltip="$t('markierte_beitraege')"
                        />
                    </v-btn-toggle>

                    <IconBtn
                        value="selectedItems"
                        color="primary"
                        :disabled="loading"
                        icon-cls="cancelmarked"
                        icon-color="white"
                        icon-disabled-color="grey"
                        :tooltip="$t('markierungen_loeschen')"
                        @click="removeSelections"
                    />
                </template>

                <template #after-query-field>
                    <div
                        :class="{
                            'ml-1': true,
                            'erw-suche-indicator': showEnhancedSearchIndicator,
                        }"
                    >
                        <IconBtn
                            color="primary"
                            :disabled="loading"
                            icon-cls="advancedsearch"
                            icon-color="white"
                            icon-disabled-color="grey"
                            :tooltip="$t('erw_suche')"
                            @click="showErwSuche = true"
                        />
                    </div>
                </template>

                <template #[`item.context_menu`]="{ item }">
                    <v-menu offset-y>
                        <template #activator="{ on, attrs }">
                            <v-btn dark icon v-bind="attrs" v-on="on">
                                <KpIcon icon-cls="moredots" color="grey" />
                            </v-btn>
                        </template>

                        <MagazinContextMenu
                            :record="item"
                            @delete-record="(record) => $refs.magazinList.initiateDelete(record)"
                            @duplicate-record="duplicateMagazin($event)"
                        />
                    </v-menu>
                </template>

                <template #[`item.image`]="{ item }">
                    <v-img :src="item.imageUrl" width="100px" height="80px" class="preview-icons">
                        <a v-if="!hidePreview" :href="item.detailPreviewUrl" :title="$t('prev_detail')" target="_blank"
                            ><i class="ic ic-20 ic-preview ic-col-black"></i
                        ></a>
                        <a v-if="!hidePreview" :href="item.searchPreviewUrl" :title="$t('prev_list')" target="_blank"
                            ><i class="ic ic-20 ic-list ic-col-black"></i
                        ></a>
                    </v-img>
                </template>
                <template #[`item.status`]="{ item }">
                    <KpIcon
                        v-if="item.publish_from.isValid() !== true"
                        icon-cls="pencil"
                        color="decline"
                        :title="$t('nicht_publiziert')"
                    />
                    <KpIcon
                        v-if="item.publish_from.isValid() && item.publish_from.isBefore(now)"
                        icon-cls="published"
                        color="ok"
                        :title="$t('publiziert')"
                    />
                    <KpIcon
                        v-if="item.publish_from.isValid() && item.publish_from.isAfter(now)"
                        icon-cls="hourglass"
                        color="neutral"
                        :title="$t('publiziert_future')"
                    />
                </template>
                <template #[`item.title`]="{ item }">
                    <list-item-title :item="item" />
                </template>
                <template #[`item.autor_title`]="{ item }">
                    <span class="entry-autor">{{ item.$.autor_title }}</span>
                </template>
                <template #[`item.frontend`]="{ item }">
                    <div v-if="item.overview_imagesize === '1'">{{ $t('klein') }}</div>
                    <div v-if="item.overview_imagesize === '2H'">{{ $t('gross') }}</div>
                    <KpIcon v-if="item.$.is_featured" icon-cls="tick" color="grey" :title="$t('getippt')" />
                    <KpIcon v-if="item.$.is_top_thema" icon-cls="star" color="grey" :title="$t('top_thema')" />
                </template>
                <template #[`item.publish_from`]="{ item }">
                    <span class="pubdate_from"
                        >{{ item.$.publish_from.isValid() ? item.publish_from.formatIf('dd, DD.MM.YYYY') : '' }}
                    </span>
                    <span v-if="item.publish_to.isValid()" class="pubdate_to"
                        >{{ $t('bis') }} <br />
                        {{ item.$.publish_to.formatIf('dd, DD.MM.YYYY') }}
                    </span>
                </template>
                <template #[`item.spezial`]="{ item }">
                    <KpIcon v-if="item.$.has_comments" icon-cls="comment" color="grey" :title="$t('has_comments')" />
                    <KpIcon v-if="item.$.has_video" icon-cls="youtube" color="grey" :title="$t('has_video')" />
                    <KpIcon
                        v-if="item.$.has_photo_gallery"
                        icon-cls="slideshow"
                        color="grey"
                        :title="$t('has_photo_gallery')"
                    />
                </template>
            </BaseRemoteList>
        </v-card>
        <v-dialog v-model="isSoftDeleteDialogVisible" scrollable persistent max-width="500px">
            <v-card>
                <v-card-title class="text-h5">{{ $t('delete_selected_dialog_title') }}</v-card-title>
                <v-card-text style="max-height: 300px">
                    <v-row v-for="item in allSelectedItems" :key="item.id">
                        <v-col>{{ item.id }}</v-col>
                        <v-col>{{ item.title }}</v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="hideSoftDeleteDialog"> {{ $t('disagree') }} </v-btn>
                    <v-btn color="secondary" text @click="agreeDeletion"> {{ $t('agree') }} </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import dayjs from 'dayjs';
import { errToStr } from '@/helpers/tools';
import { instance as api } from '@/helpers/api';
import { MagazinCollection } from '@/models/Magazin';
import sessionStorageMixin from '@/mixins/session-storage';
import ListItemTitle from '@/components/magazin/list-item-title.vue';
import MagazinEnhancedSearch from '@/components/magazin/EnhancedSearch.vue';
import MagazinContextMenu from '@/components/magazin/MagazinContextMenu.vue';
import { globalParam } from '@/helpers/globals';

export default {
    name: 'MagazinIndex',

    components: {
        ListItemTitle,
        MagazinEnhancedSearch,
        MagazinContextMenu,
    },
    mixins: [sessionStorageMixin],

    data() {
        this.sessionKey = 'magazin-index';
        return {
            loading: false,
            MagazinCollection,
            headers: [
                { width: 35, sortable: false, value: 'context_menu' },
                { text: 'Status', sortable: false, width: 80, value: 'status' },
                { text: 'Bild', value: 'image', width: 100, sortable: false },
                { text: this.$t('titel'), sortable: true, value: 'title' },
                { text: this.$t('spezial'), width: 130, sortable: false, value: 'spezial' },
                { text: this.$t('autor'), width: 120, sortable: false, value: 'autor_title' },
                { text: this.$t('order_number'), width: 135, sortable: true, value: '$.order_number' },
                { text: this.$t('frontend'), width: 100, sortable: false, value: 'frontend' },
                { text: this.$t('pubdatum'), width: 180, sortable: true, value: 'publish_from' },
                { text: this.$t('dossier'), width: 130, sortable: true, value: '$.dossiers' },
            ],
            selectedItems: [], // selected records on current page
            selected: this.getSessionValue('selected') || [], // global selected records
            toggleFilters: this.getSessionValue('toggleFilters') || [],
            isPublishedFilter: this.getSessionValue('isPublishedFilter') || null,

            extendedSearchParams: this.getSessionValue('extendedSearchParams') || {},

            // will be set when extended search btn is clicked --> initially taken from extended SearchParams
            enhancedFilters: this.getSessionValue('extendedSearchParams') || null,
            showErwSuche: false,
            allSelectedItems: [],
            isSoftDeleteDialogVisible: false,
        };
    },
    computed: {
        filters() {
            const filters = [];
            if (this.toggleFilters.indexOf('selectedItems') >= 0) {
                filters.push({
                    property: 'id',
                    value: [...(this.selected.length ? this.selected : [-1])],
                    // TODO: When rewrite Filter Handling: operator: 'IN',
                });
            }
            return filters;
        },

        extraParams() {
            const toggleFilterList = ['btn_reg', 'btn_front', 'btn_mar'];
            const extraParams = {};
            const enhancedFilters = {};
            toggleFilterList.forEach((ident) => {
                if (this.toggleFilters.indexOf(ident) >= 0) {
                    extraParams[ident] = true;
                }
            });

            for (let key in this.enhancedFilters) {
                if (this.enhancedFilters[key]) {
                    enhancedFilters[key] = this.enhancedFilters[key];
                }
            }
            if (this.isPublishedFilter === 'is_published') {
                enhancedFilters.status = 'published';
            }
            if (this.isPublishedFilter === 'is_not_published') {
                enhancedFilters.status = 'unpublished';
            }
            if (this.isPublishedFilter === 'is_published_future') {
                enhancedFilters.status = 'published_future';
            }
            extraParams.enhancedFilters = enhancedFilters;
            return extraParams;
        },

        showEnhancedSearchIndicator() {
            let show = false;
            for (const key in this.enhancedFilters || {}) {
                if (!Array.isArray(this.enhancedFilters[key]) || this.enhancedFilters[key].length > 0) {
                    show = true;
                }
            }
            return show;
        },

        hidePreview() {
            return globalParam('magazin.hide_preview');
        },
    },

    watch: {
        toggleFilters(val) {
            this.setSessionValue('toggleFilters', [...val]);
        },
        isPublishedFilter(val) {
            if (val) {
                this.setSessionValue('isPublishedFilter', val);
            } else {
                this.clearSessionValue('isPublishedFilter');
            }
        },
    },
    created() {
        this.now = dayjs();
    },
    methods: {
        onEdit(magazin) {
            this.openEntry(magazin);
        },

        onRowDblClick(e, data) {
            if (data.item) {
                this.openEntry(data.item);
            }
        },

        onItemSelected({ item, value }) {
            const newIds = [...this.selected];
            if (value === true) {
                newIds.push(item.id);
            } else {
                delete newIds[newIds.indexOf(item.id)];
            }

            // unique and filter nulls
            this.selected = [...new Set(newIds.filter(Boolean))];
            this.setSessionValue('selected', this.selected);
        },
        onLoaded(collection) {
            // re-select the selected items from the session id array:
            let selectedItems = [];
            if (this.selected.length) {
                selectedItems = collection.models.filter((item) => this.selected.indexOf(item.id) > -1);
            }
            this.selectedItems = selectedItems;
        },

        removeSelections() {
            this.selected = [];
            this.toggleFilters = this.toggleFilters.filter((item) => item !== 'selectedItems');
            this.setSessionValue('selected', this.selected);
        },

        openEntry(magazin) {
            // On existing records, we route to the info-category panel directly:
            this.$router.push({ name: 'magazin-edit', params: { id: magazin.id, backRoute: { route: this.$route } } });
        },

        onNew() {
            this.$router.push({ name: 'magazin-edit', params: { id: 'new' } });
        },

        onEnhancedSearch(searchParams) {
            this.extendedSearchParams = searchParams;
            this.setSessionValue('extendedSearchParams', { ...this.extendedSearchParams });
            this.enhancedFilters = { ...this.extendedSearchParams };
            this.showErwSuche = false;
        },

        resetEnhancedSearch() {
            this.extendedSearchParams = {};
            this.setSessionValue('extendedSearchParams', { ...this.extendedSearchParams });
            this.enhancedFilters = { ...this.extendedSearchParams };
        },

        async duplicateMagazin(magazin) {
            this.loading = true;
            try {
                await api().post('/magazin/duplicate', {
                    id: magazin.id,
                });
                this.$refs.magazinList.reload();
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.loading = false;
            }
        },

        async loadAllSelectedItems() {
            const newColl = new MagazinCollection();
            const res = await newColl.query({ id: this.selected });
            this.allSelectedItems = res.models;
        },

        async showSoftDeleteDialog() {
            await this.loadAllSelectedItems();
            if (!this.allSelectedItems.length) {
                this.allSelectedItems = [];
            } else {
                this.isSoftDeleteDialogVisible = true;
            }
        },

        hideSoftDeleteDialog() {
            this.isSoftDeleteDialogVisible = false;
        },

        agreeDeletion() {
            this.$refs.magazinList.deleteItems(this.allSelectedItems);
            this.removeSelections();
            this.hideSoftDeleteDialog();
        },
    },
};
</script>

<style lang="scss" scoped>
.magazin-list {
    ::v-deep table > tbody > tr > td {
        vertical-align: top;
        color: var(--gray-text-color);
    }
}
.entry-autor {
    font-style: italic;
}
.pubdate_from {
    color: var(--nearblack-color);
}

.preview-icons {
    ::v-deep {
        .v-responsive__content {
            display: flex;
            align-items: flex-end;
            justify-content: flex-end;

            a {
                width: 22px;
                height: 22px;
                display: inline-block;
                background-color: var(--tgk-grey-5);
                opacity: 0.4;
                .ic {
                    opacity: 0.4;
                }

                &:hover {
                    opacity: 1;
                    .ic {
                        opacity: 1;
                    }
                }
            }
        }
    }
}
</style>
